import { MaterialCommunityIcons } from "@expo/vector-icons";
import { LinearGradient } from "expo-linear-gradient";
import { useLocalSearchParams, useRouter } from "expo-router";
import { useEffect, useState } from "react";
import { ActivityIndicator, StyleSheet, TouchableOpacity, View } from "react-native";
import { Text } from "react-native-paper";
import { SafeAreaView } from "react-native-safe-area-context";

import { useAuth } from "@/lib/auth";
import { getLeadByPhone } from "@/lib/leads-api";
import { colors, fonts, gradients, radius, shadows, spacing } from "@/lib/theme";

/**
 * Shown after OTP when the phone is NOT recognized. The nurse chooses to either
 * start onboarding (new applicant) or, if they already have an account, request
 * that this new number be added to it.
 */
export default function AccountChoiceScreen() {
    const router = useRouter();
    const { phone } = useLocalSearchParams<{ phone: string }>();
    const { pendingPhone } = useAuth();
    const activePhone = phone || pendingPhone || "";
    const [checking, setChecking] = useState(true);

    // If this phone already has a landing-site lead, the nurse is a known
    // applicant continuing from the web — skip the fork and go straight to
    // register (pre-filled). The fork is only for genuinely unknown numbers.
    useEffect(() => {
        if (!activePhone) {
            setChecking(false);
            return;
        }
        let cancelled = false;
        void getLeadByPhone(activePhone).then((lead) => {
            if (cancelled) {
                return;
            }
            if (lead) {
                router.replace({ pathname: "/(auth)/register", params: { phone: activePhone } });
            } else {
                setChecking(false);
            }
        });
        return () => {
            cancelled = true;
        };
    }, [activePhone, router]);

    if (checking) {
        return (
            <SafeAreaView style={[styles.container, styles.centered]}>
                <ActivityIndicator size="large" color={colors.teal400} />
            </SafeAreaView>
        );
    }

    return (
        <SafeAreaView style={styles.container}>
            <View style={styles.content}>
                <View style={styles.header}>
                    <Text style={styles.title}>Welcome to Task Health</Text>
                    <Text style={styles.subtitle}>
                        We don&apos;t recognize this number yet. What would you like to do?
                    </Text>
                </View>

                <TouchableOpacity
                    activeOpacity={0.85}
                    onPress={() =>
                        router.push({
                            pathname: "/(auth)/register",
                            params: { phone: activePhone }
                        })
                    }
                >
                    <LinearGradient
                        colors={gradients.brand}
                        start={{ x: 0, y: 0 }}
                        end={{ x: 1, y: 0 }}
                        style={styles.primaryCard}
                    >
                        <MaterialCommunityIcons name="account-plus" size={26} color="#fff" />
                        <View style={styles.cardText}>
                            <Text style={styles.primaryTitle}>Start onboarding</Text>
                            <Text style={styles.primarySubtitle}>
                                I&apos;m new — set up my nurse profile
                            </Text>
                        </View>
                    </LinearGradient>
                </TouchableOpacity>

                <TouchableOpacity
                    activeOpacity={0.85}
                    style={styles.secondaryCard}
                    onPress={() =>
                        router.push({
                            pathname: "/(auth)/add-number",
                            params: { phone: activePhone }
                        })
                    }
                >
                    <MaterialCommunityIcons name="cellphone-cog" size={26} color={colors.teal600} />
                    <View style={styles.cardText}>
                        <Text style={styles.secondaryTitle}>I already have an account</Text>
                        <Text style={styles.secondarySubtitle}>
                            Add this number to my existing account
                        </Text>
                    </View>
                </TouchableOpacity>
            </View>
        </SafeAreaView>
    );
}

const styles = StyleSheet.create({
    container: { flex: 1, backgroundColor: colors.background },
    centered: { alignItems: "center", justifyContent: "center" },
    content: { flex: 1, paddingHorizontal: spacing.lg, justifyContent: "center", gap: spacing.md },
    header: { marginBottom: spacing.lg },
    title: { fontFamily: fonts.bold, fontSize: 26, color: colors.textPrimary },
    subtitle: {
        fontFamily: fonts.regular,
        fontSize: 15,
        color: colors.textSecondary,
        marginTop: spacing.xs
    },
    primaryCard: {
        flexDirection: "row",
        alignItems: "center",
        gap: spacing.md,
        padding: spacing.lg,
        borderRadius: radius.lg,
        ...shadows.sm
    },
    secondaryCard: {
        flexDirection: "row",
        alignItems: "center",
        gap: spacing.md,
        padding: spacing.lg,
        borderRadius: radius.lg,
        backgroundColor: colors.surface,
        borderWidth: 1,
        borderColor: colors.border
    },
    cardText: { flex: 1 },
    primaryTitle: { fontFamily: fonts.bold, fontSize: 17, color: "#fff" },
    primarySubtitle: { fontFamily: fonts.regular, fontSize: 13, color: "rgba(255,255,255,0.9)" },
    secondaryTitle: { fontFamily: fonts.bold, fontSize: 17, color: colors.textPrimary },
    secondarySubtitle: { fontFamily: fonts.regular, fontSize: 13, color: colors.textSecondary }
});
